import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBarChart2, FiArrowRight, FiPieChart, FiTrendingUp,
  FiUsers, FiClock
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AnalyticsDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/analytics" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Analytics</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl mb-4">
              <FiBarChart2 className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Analytics & Insights</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Gain valuable insights into productivity, project progress, and team performance with built-in analytics.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Data-Driven Decisions</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tasklina analytics helps you understand how work is progressing, identify bottlenecks, 
                and make informed decisions to improve productivity and team performance.
              </p>
            </div>
          </section>

          {/* Analytics Types */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Analytics</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiPieChart className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Task Distribution</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">See how tasks are distributed by status, priority, and assignee</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiTrendingUp className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Progress Tracking</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Track project completion rates and velocity over time</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUsers className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Team Performance</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Understand workload distribution and individual contributions</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiClock className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Time Analysis</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Analyze task completion times and estimate accuracy</p>
              </div>
            </div>
          </section>

          {/* Dashboard Widgets */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Dashboard Widgets</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Tasks Overview</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  A summary widget showing total tasks, completed tasks, overdue tasks, and tasks due soon. 
                  Perfect for a quick status check.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Burndown Chart</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Visualize project progress with a burndown chart showing completed vs remaining work 
                  against time.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Workload Distribution</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  See how tasks are distributed among team members to identify overloaded individuals 
                  and balance workload.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Activity Feed</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Real-time feed of recent activity including task updates, comments, and completions.
                </p>
              </div>
            </div>
          </section>

          {/* Personal vs Organization Analytics */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Analytics Scope</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Personal Analytics</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Your task completion rate</li>
                  <li>• Personal productivity trends</li>
                  <li>• Time tracking insights</li>
                  <li>• Project involvement summary</li>
                </ul>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Organization Analytics</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Team-wide performance metrics</li>
                  <li>• Department comparisons</li>
                  <li>• Organization-wide trends</li>
                  <li>• Resource utilization reports</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Exporting Reports */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Exporting Reports</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Export analytics data for presentations, stakeholder reports, or further analysis.
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li>• <strong>PDF Reports:</strong> Formatted reports for sharing</li>
                <li>• <strong>CSV Export:</strong> Raw data for spreadsheet analysis</li>
                <li>• <strong>Scheduled Reports:</strong> Automatic weekly/monthly email reports</li>
              </ul>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/analytics"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Organization Analytics</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AnalyticsDocs;
